
import axios from 'axios'
import { format } from 'date-fns'
import { computed, defineComponent, inject, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { AuthPlugin } from '@/auth'
import print from 'vue3-print-nb'
import { EditPen, Check, Close } from '@element-plus/icons-vue'

interface PredictionResult {
  BMS: number
  CORN?: {
    logits: Array<number>
    probabilities: Array<number>
  }
}

interface TimeStamp {
  createdAt?: string
  updatedAt?: string
}

interface User extends TimeStamp {
  id: string
  email: string
  name?: string
}

interface Example extends TimeStamp {
  bms: number
  image?: string
  trapeziusThickness: number
  trapeziusSubcutaneousFatThickness: number
  subcutaneousFatThickness: number
  latissimusThickness: number
}

interface Cattle extends TimeStamp {
  id: string
  farmer?: string
  birthday?: string
  sex?: string
  father?: string
  mothersFather?: string
  mothersMothersFather?: string
}

interface carcass extends TimeStamp {
  barcodeNo: string
  bodyNo: string
  cattleId: string
  crossSectionImgHash: string
  crossSectionImgUrl: string
  crossSectionRibeyeFatMaskUrl: string
  crossSectionRibeyeMaskUrl: string
  dms: number
  marblingRate: number
  newFinenessIndex: number
  nfi2: number
  ownerName: string
  pk: string
  ribEyeArea: number
  takenAt: string
  ribEyeAreaByGrader: number
  ribThickness: number
  subcutaneousFatThickness: number
  yieldScore: number
  wholeWeight: number
  bmsNo: number
}

interface Prediction extends TimeStamp {
  id: number
  userId: string
  user: User
  cattleId: string
  cattle: Cattle
  shotAt: string
  shootingDate: string
  result: PredictionResult
  exampleSearch?: Example
  carcass?: carcass
}

export default defineComponent({
  name: 'Detail',
  directives: {
    print,
  },
  setup() {
    const route = useRoute()
    const auth: AuthPlugin | undefined = inject('Auth')
    if (!auth) {
      throw new Error('Auth plugin is not provided')
    }
    const backendUrl: string | undefined = inject('BackendUrl')
    if (!backendUrl) {
      throw new Error('BackendUrl is not provided')
    }
    const predictionBaseUrl: string | undefined = inject('PredictionsUrl')
    if (!predictionBaseUrl) {
      throw new Error('PredictionsUrl is not provided')
    }

    const prediction = ref<Prediction>()
    const predictionUrl = ref()
    const example = ref<Example>()

    const trapeziusThickness = ref<number>()
    const trapeziusSubcutaneousFatThickness = ref<number>()
    const subcutaneousFatThickness = ref<number>()
    const latissimusThickness = ref<number>()
    const isEditing = ref(false)
    const isReloadingCattleInfo = ref(false)

    const moonAge = computed(() => {
      if (prediction?.value?.shootingDate == null || prediction?.value?.cattle?.birthday == null) {
        return null
      }

      const shootingDate = new Date(prediction.value.shootingDate)
      const birthday = new Date(prediction.value.cattle.birthday)

      const moonAge =
        (shootingDate.getFullYear() - birthday.getFullYear()) * 12 +
        shootingDate.getMonth() -
        birthday.getMonth()

      return moonAge < 0 ? 0 : moonAge
    })

    const printObj = reactive({ id: 'detail' })

    const display = (
      value?: unknown,
      option: { prefix?: string; suffix?: string; placeholder?: string } = {},
    ): string => {
      const _option = { prefix: '', suffix: '', placeholder: '-', ...option }
      return value != null ? `${_option.prefix}${value}${_option.suffix}` : _option.placeholder
    }

    const formatDate = (date?: string, pattern = 'yyyy-MM-dd HH:mm:ss') => {
      return date && format(new Date(date), pattern)
    }

    const getPrediction = async () => {
      const idToken = (await auth.getIdTokenClaims({}))?.__raw

      const response = await axios.get(`${backendUrl}/v1/prediction/${route.params.id}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      })

      prediction.value = response.data
      console.log(prediction.value)
      console.log(prediction.value?.carcass)

      const userId = prediction.value?.userId
      const cattleId = prediction.value?.cattleId
      const shootingDate = prediction.value?.shootingDate.replaceAll('-', '')
      predictionUrl.value = `${predictionBaseUrl}/${userId}/${cattleId}-${shootingDate}`
    }

    const reloadCattleInfo = async () => {
      if (!prediction.value?.cattleId) {
        alert('個体識別番号が未設定のため、個体情報を再取得できません。')
        return
      }

      isReloadingCattleInfo.value = true
      try {
        const idToken = (await auth.getIdTokenClaims({}))?.__raw

        await axios.put(
          `${backendUrl}/v1/cattle/${prediction.value.cattleId}/cattle-search`,
          null,
          {
            headers: { Authorization: `Bearer ${idToken}` },
          },
        )

        await getPrediction()
      } catch (error) {
        console.error('個体情報再取得エラー:', error)
        alert('個体情報の再取得に失敗しました')
      } finally {
        isReloadingCattleInfo.value = false
      }
    }

    const getRank5Percentage = (): number | undefined => {
      const rank5proba = prediction.value?.result?.CORN?.probabilities[4]
      if (!rank5proba) return undefined
      
      // 5等級以上割合の計算（BMS 8以上の確率）
      const bms8BelowProbability = Math.round(rank5proba * 10) * 10
      const rank5Percentage = 100 - bms8BelowProbability
      return Math.max(10, Math.min(90, rank5Percentage))
    }

    const getExample = async () => {
      const idToken = (await auth.getIdTokenClaims({}))?.__raw
      const bms = prediction.value?.result.BMS
      const tt = prediction.value?.exampleSearch?.trapeziusThickness
      const tsft = prediction.value?.exampleSearch?.trapeziusSubcutaneousFatThickness
      const sft = prediction.value?.exampleSearch?.subcutaneousFatThickness
      const lt = prediction.value?.exampleSearch?.latissimusThickness

      if (bms == null || tt == null || tsft == null || lt == null) {
        return
      }

      const response = await axios.get(`${backendUrl}/v1/example`, {
        headers: { Authorization: `Bearer ${idToken}` },
        params: { bms, tt, tsft, sft, lt },
      })
      example.value = response.data[0]
    }

    const toggleEditing = async () => {
      if (prediction.value?.exampleSearch) {
        trapeziusThickness.value = prediction.value.exampleSearch.trapeziusThickness
        trapeziusSubcutaneousFatThickness.value =
          prediction.value.exampleSearch.trapeziusSubcutaneousFatThickness
        subcutaneousFatThickness.value = prediction.value.exampleSearch.subcutaneousFatThickness
        latissimusThickness.value = prediction.value.exampleSearch.latissimusThickness
      }
      isEditing.value = true
    }

    const doEditing = async () => {
      // 入力検証関数
      const validateFieldValue = (fieldName: string, value: any): string | null => {
        // None または空文字チェック
        if (value === null || value === undefined || value === '') {
          return `${fieldName}を入力してください`
        }
        
        // 数値変換チェック
        const numValue = Number(value)
        if (isNaN(numValue)) {
          return `${fieldName}は数値で入力してください`
        }
        
        // 正の値チェック
        if (numValue <= 0) {
          return `${fieldName}は正の値で入力してください`
        }
        
        return null
      }

      // 各フィールドの検証
      const validationErrors = []
      const fields = [
        { name: 'BMS', value: prediction.value?.result.BMS },
        { name: '僧帽筋厚', value: trapeziusThickness.value },
        { name: '僧帽筋皮下脂肪厚', value: trapeziusSubcutaneousFatThickness.value },
        { name: '皮下脂肪厚', value: subcutaneousFatThickness.value },
        { name: '広背筋厚', value: latissimusThickness.value }
      ]

      for (const field of fields) {
        const error = validateFieldValue(field.name, field.value)
        if (error) {
          validationErrors.push(error)
        }
      }

      // 検証エラーがある場合はアラートを表示して処理を停止
      if (validationErrors.length > 0) {
        alert('入力エラー:\n' + validationErrors.join('\n'))
        return
      }

      if (
        prediction.value &&
        trapeziusThickness.value &&
        trapeziusSubcutaneousFatThickness.value &&
        subcutaneousFatThickness.value &&
        latissimusThickness.value
      ) {
        prediction.value.exampleSearch = {
          bms: prediction.value.result.BMS,
          trapeziusThickness: trapeziusThickness.value,
          trapeziusSubcutaneousFatThickness: trapeziusSubcutaneousFatThickness.value,
          subcutaneousFatThickness: subcutaneousFatThickness.value,
          latissimusThickness: latissimusThickness.value,
        }
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        
        try {
          await axios.put(
            `${backendUrl}/v1/prediction/${prediction.value.id}/example`,
            prediction.value?.exampleSearch,
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
              },
            },
          )
        } catch (error: any) {
          console.error('保存エラー:', error)
          if (error.response?.data?.details) {
            alert('保存エラー:\n' + error.response.data.details.join('\n'))
          } else {
            alert('保存に失敗しました')
          }
        }
      }

      isEditing.value = false
    }

    const cancelEditing = async () => {
      isEditing.value = false
    }

    const setTitle = async () => {
      const cattleId = prediction.value?.cattleId || 'unknown'
      const shootingDate = prediction.value?.shootingDate.replaceAll('-', '') || 'unknown'
      document.title = `${shootingDate}-${cattleId}`
    }

    ;(async () => {
      await getPrediction()
      await getExample()
      await setTitle()
    })()

    return {
      prediction,
      example,
      moonAge,
      predictionUrl,
      printObj,
      display,
      formatDate,
      getRank5Percentage,
      trapeziusThickness,
      trapeziusSubcutaneousFatThickness,
      subcutaneousFatThickness,
      latissimusThickness,
      isEditing,
      isReloadingCattleInfo,
      doEditing,
      cancelEditing,
      toggleEditing,
      reloadCattleInfo,
      EditPen,
      Check,
      Close,
    }
  },
})
